/**
 * powWorker.js — 动态 PoW 客户端求解（与 risk.powChallenge 难度阶梯对齐）
 *
 * 职责：拉取挑战 → 本地碰撞求解 → 组装 botProof.pow。
 * 不负责 Turnstile / 生物动力学（见 botDetector.js）。
 *
 * 求解策略（2026-09-06 · TP WebView 兼容）：
 * - 服务端封顶 DiffHeavyBomb=14；凡 difficulty<=14 一律主线程纯 JS SHA-256 同步求解（通常 <50ms）。
 * - 禁止依赖 WebCrypto 逐次 await（移动 WebView 会卡死超时 → pow_missing → 816）。
 * - 禁止在常规难度下创建 Blob Worker（钱包 DApp 沙箱常禁 Worker → 旧路径失败）。
 * - difficulty>14 仅作未来扩展：优先 Worker，失败再同步兜底。
 */

import request from '@/utils/request';
import { resolveApiUrl } from '@/utils/backend-prefix';

/** 与 auth 公开接口一致：不附旧 JWT、401 不踢登（避免与 auth.js 循环依赖） */
const POW_PUBLIC_REQUEST_CONFIG = {
  _skipAuthHeader: true,
  _skipAuthRefresh: true,
};

/** 与 deepsage-go risk.DiffCalm 对齐：平峰（16 bits）始终走同步求解，确保钱包 WebView 不受 Worker 限制 */
export const POW_SYNC_MAX_DIFFICULTY = 16;

function rightRotate(value, amount) {
  return (value >>> amount) | (value << (32 - amount));
}

const mathPow = Math.pow;
const maxWord = mathPow(2, 32);
const hashInit = [];
const kInit = [];
{
  let primeCounter = 0;
  const isComposite = {};
  for (let candidate = 2; primeCounter < 64; candidate += 1) {
    if (!isComposite[candidate]) {
      for (let i = 0; i < 313; i += candidate) {
        isComposite[i] = candidate;
      }
      hashInit[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      kInit[primeCounter] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
      primeCounter += 1;
    }
  }
}

/**
 * 纯 JS 同步 SHA-256（hex 小写）。算法与 Worker / 服务端 crypto/sha256 碰撞语义一致。
 * @param {string} message
 * @returns {string}
 */
export function sha256Sync(message) {
  const input = String(message ?? '');
  const asciiBitLength = input.length * 8;
  let ascii = `${input}\x80`;
  while ((ascii.length % 64) - 56) ascii += '\x00';

  const words = [];
  for (let i = 0; i < ascii.length; i += 1) {
    words[i >> 2] |= ascii.charCodeAt(i) << (((3 - i) % 4) * 8);
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength | 0;

  let hash = hashInit.slice(0);
  const k = kInit;
  for (let j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash;
    hash = hash.slice(0, 8);
    for (let i = 0; i < 64; i += 1) {
      const w15 = w[i - 15];
      const w2 = w[i - 2];
      const s0 = rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3);
      const s1 = rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10);
      w[i] = i < 16 ? w[i] : (w[i - 16] + s0 + w[i - 7] + s1) | 0;
      const s1h = rightRotate(hash[4], 6) ^ rightRotate(hash[4], 11) ^ rightRotate(hash[4], 25);
      const ch = (hash[4] & hash[5]) ^ (~hash[4] & hash[6]);
      const temp1 = (hash[7] + s1h + ch + k[i] + w[i]) | 0;
      const s0h = rightRotate(hash[0], 2) ^ rightRotate(hash[0], 13) ^ rightRotate(hash[0], 22);
      const maj = (hash[0] & hash[1]) ^ (hash[0] & hash[2]) ^ (hash[1] & hash[2]);
      const temp2 = (s0h + maj) | 0;
      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
      hash.pop();
    }
    for (let i = 0; i < 8; i += 1) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }

  let result = '';
  for (let i = 0; i < 8; i += 1) {
    for (let j = 3; j >= 0; j -= 1) {
      const b = (hash[i] >> (8 * j)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result;
}

/**
 * @param {string} hexStr
 * @param {number} bits
 * @returns {boolean}
 */
export function hasLeadingZeroBitsHex(hexStr, bits) {
  if (!bits || bits <= 0) return true;
  const hexZeros = Math.floor(bits / 4);
  const remBits = bits % 4;
  for (let i = 0; i < hexZeros; i += 1) {
    if (hexStr[i] !== '0') return false;
  }
  if (remBits > 0) {
    const val = parseInt(hexStr[hexZeros], 16);
    const maxAllowed = (1 << (4 - remBits)) - 1;
    if (val > maxAllowed) return false;
  }
  return true;
}

/**
 * 主线程同步求解（TP / 任意 WebView 安全路径）。
 * @param {string} salt
 * @param {number} difficulty
 * @param {number} [maxIters]
 */
export function solvePoWSync(salt, difficulty, maxIters = 2000000) {
  const dif = Number(difficulty);
  if (!salt || !Number.isFinite(dif) || dif < 0) {
    return { success: false, reason: 'invalid_params' };
  }
  const limit = Math.max(1, Math.floor(maxIters));
  const t0 = Date.now();
  for (let i = 0; i < limit; i += 1) {
    const nonce = String(i);
    const hash = sha256Sync(salt + nonce);
    if (hasLeadingZeroBitsHex(hash, dif)) {
      return {
        success: true,
        nonce,
        hash,
        iters: i,
        durationMs: Date.now() - t0,
      };
    }
  }
  return {
    success: false,
    reason: 'exceeded_max_iters',
    durationMs: Date.now() - t0,
  };
}

// 仅 difficulty > POW_SYNC_MAX_DIFFICULTY 时使用（当前服务端封顶 14，生产路径不会进入）
const WORKER_CODE = `
function rightRotate(v, a) { return (v >>> a) | (v << (32 - a)); }
var mathPow = Math.pow, maxWord = mathPow(2, 32);
var hashInit = [], kInit = [], primeCounter = 0, isComposite = {};
for (var candidate = 2; primeCounter < 64; candidate++) {
  if (!isComposite[candidate]) {
    for (var i = 0; i < 313; i += candidate) isComposite[i] = candidate;
    hashInit[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
    kInit[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
  }
}
function sha256(ascii) {
  var result = '', words = [], asciiBitLength = ascii.length * 8;
  var hash = hashInit.slice(0), k = kInit;
  ascii += '\\x80';
  while ((ascii.length % 64) - 56) ascii += '\\x00';
  for (var i = 0; i < ascii.length; i++) words[i >> 2] |= ascii.charCodeAt(i) << (((3 - i) % 4) * 8);
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength | 0;
  for (var j = 0; j < words.length; ) {
    var w = words.slice(j, (j += 16)), oldHash = hash;
    hash = hash.slice(0, 8);
    for (var i = 0; i < 64; i++) {
      var w15 = w[i - 15], w2 = w[i - 2];
      var s0 = rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3);
      var s1 = rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10);
      w[i] = (i < 16) ? w[i] : (w[i - 16] + s0 + w[i - 7] + s1) | 0;
      var s1h = rightRotate(hash[4], 6) ^ rightRotate(hash[4], 11) ^ rightRotate(hash[4], 25);
      var ch = (hash[4] & hash[5]) ^ (~hash[4] & hash[6]);
      var temp1 = (hash[7] + s1h + ch + k[i] + w[i]) | 0;
      var s0h = rightRotate(hash[0], 2) ^ rightRotate(hash[0], 13) ^ rightRotate(hash[0], 22);
      var maj = (hash[0] & hash[1]) ^ (hash[0] & hash[2]) ^ (hash[1] & hash[2]);
      var temp2 = (s0h + maj) | 0;
      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
      hash.pop();
    }
    for (var i = 0; i < 8; i++) hash[i] = (hash[i] + oldHash[i]) | 0;
  }
  for (var i = 0; i < 8; i++) {
    for (var j = 3; j >= 0; j--) {
      var b = (hash[i] >> (8 * j)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result;
}
function hasLeadingZeroBits(hexStr, bits) {
  var hexZeros = Math.floor(bits / 4);
  var remBits = bits % 4;
  for (var i = 0; i < hexZeros; i++) { if (hexStr[i] !== '0') return false; }
  if (remBits > 0) {
    var val = parseInt(hexStr[hexZeros], 16);
    var maxAllowed = (1 << (4 - remBits)) - 1;
    if (val > maxAllowed) return false;
  }
  return true;
}
self.onmessage = function(e) {
  var salt = e.data.salt || '';
  var difficulty = e.data.difficulty || 8;
  var maxIters = e.data.maxIters || 4000000;
  var t0 = Date.now();
  for (var i = 0; i < maxIters; i++) {
    var nonce = i.toString();
    var hash = sha256(salt + nonce);
    if (hasLeadingZeroBits(hash, difficulty)) {
      self.postMessage({ success: true, nonce: nonce, hash: hash, iters: i, durationMs: Date.now() - t0 });
      return;
    }
  }
  self.postMessage({ success: false, reason: 'exceeded_max_iters', durationMs: Date.now() - t0 });
};
`;

function solvePoWInWorker(salt, difficulty, maxIters, timeoutMs) {
  return new Promise((resolve) => {
    let worker = null;
    let workerUrl = null;
    let timer = null;
    const cleanup = () => {
      if (timer) clearTimeout(timer);
      if (worker) {
        worker.terminate();
        worker = null;
      }
      if (workerUrl && typeof URL !== 'undefined') {
        URL.revokeObjectURL(workerUrl);
        workerUrl = null;
      }
    };
    try {
      if (typeof Worker === 'undefined' || typeof Blob === 'undefined') {
        resolve(solvePoWSync(salt, difficulty, maxIters));
        return;
      }
      const blob = new Blob([WORKER_CODE], { type: 'application/javascript' });
      workerUrl = URL.createObjectURL(blob);
      worker = new Worker(workerUrl);
      timer = setTimeout(() => {
        cleanup();
        resolve(solvePoWSync(salt, difficulty, maxIters));
      }, timeoutMs);
      worker.onmessage = (e) => {
        cleanup();
        resolve(e.data);
      };
      worker.onerror = () => {
        cleanup();
        resolve(solvePoWSync(salt, difficulty, maxIters));
      };
      worker.postMessage({ salt, difficulty, maxIters });
    } catch {
      cleanup();
      resolve(solvePoWSync(salt, difficulty, maxIters));
    }
  });
}

/**
 * @param {{ salt: string, difficulty: number, maxIters?: number, timeoutMs?: number }} options
 */
export function solvePoW({ salt, difficulty, maxIters = 4000000, timeoutMs = 25000 }) {
  const dif = Number(difficulty);
  if (!salt || !Number.isFinite(dif)) {
    return Promise.resolve({ success: false, reason: 'invalid_params' });
  }

  // 平峰 16 bits：始终主线程同步快速碰撞（平均 6.5 万次，约 100~200ms），避免钱包 WebView Worker/CSP 失败
  if (dif <= POW_SYNC_MAX_DIFFICULTY) {
    const syncMax = Math.min(maxIters, 1000000);
    return Promise.resolve(solvePoWSync(salt, dif, syncMax));
  }

  // 高于 16（18~20+ 攻击态）：优先 Worker 碰撞避免卡顿，Worker 不可用/失败时自动降级同步兜底
  return solvePoWInWorker(salt, dif, maxIters, timeoutMs);
}

/**
 * GET /liyuan-auth/pow/challenge（经 withBackendPrefix → /app/liyuan-auth/...）
 * @param {string} [action]
 */
export async function fetchPoWChallenge(action = 'auth_login') {
  const act = String(action || 'auth_login');
  try {
    const res = await request({
      url: '/liyuan-auth/pow/challenge',
      method: 'get',
      params: { action: act, _ts: Date.now() },
      timeout: 20000,
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      ...POW_PUBLIC_REQUEST_CONFIG,
    });
    const d = res?.data || res;
    if (d && d.challengeId && d.salt) {
      return d;
    }
  } catch (err) {
    console.warn('[pow] fetch_challenge_failed', { action: act, err: String(err?.message || err) });
  }

  // axios 失败时用原生 fetch 再试（部分 WebView 对 XHR 拦截更严）
  try {
    const url = resolveApiUrl(
      `/liyuan-auth/pow/challenge?action=${encodeURIComponent(act)}&_ts=${Date.now()}`
    );
    const resp = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
    });
    if (!resp.ok) {
      throw new Error(`http_${resp.status}`);
    }
    const body = await resp.json();
    const d = body?.data || body;
    if (d && d.challengeId && d.salt) {
      return d;
    }
  } catch (err) {
    console.warn('[pow] fetch_challenge_native_failed', {
      action: act,
      err: String(err?.message || err),
    });
  }
  return null;
}

/**
 * @param {string} [action]
 * @returns {Promise<{cid:string,nonce:string,salt:string,dif:number,exp:number,sig:string,serverTicket?:string}|null>}
 */
export async function getPoWPayload(action = 'auth_login') {
  let chal = null;
  for (let attempt = 0; attempt < 3 && !chal; attempt += 1) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, 300 * attempt));
    }
    chal = await fetchPoWChallenge(action);
  }
  if (!chal) {
    console.warn('[pow] challenge_unavailable', { action });
    return null;
  }

  const dif = Number(chal.difficulty);
  const res = await solvePoW({
    salt: chal.salt,
    difficulty: dif,
  });

  if (res && res.success && res.nonce) {
    const serverTicket = String(chal.serverTicket || '').trim();
    return {
      cid: chal.challengeId,
      nonce: res.nonce,
      salt: chal.salt,
      dif,
      exp: chal.expireAt,
      sig: chal.signature,
      ...(serverTicket ? { serverTicket } : {}),
    };
  }

  console.warn('[pow] solve_failed', {
    action,
    difficulty: dif,
    reason: res?.reason || 'unknown',
    durationMs: res?.durationMs,
  });
  return null;
}
